import React from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import CustomerTypeCell from './CustomerTypeCell';
import { formatDate, formatDateTime } from '../../utils/formatDate';
import { woTypeValue, woTypeLabel } from '../../utils/workOrderEdit';
import { woSchedule } from '../../utils/woSchedule';

/**
 * WorkOrderHeaderStrip — IDI-186 (paired closes IDI-188).
 *
 * A view-mode-only strip that mounts between DetailPageHeader and
 * GoldenCustomerCard on the Work Order detail page. Five cells:
 *
 *     Type · Job # · Customer Type · Start · Stop
 *
 * ─── Consolidation, not addition ─────────────────────────────────────────
 *
 * v2 BUILD SCOPE was explicit: this strip REPLACES three surfaces:
 *
 *   1. The right-rail "WO Info Card" (WorkOrderDetail.js:1968) — retired
 *      in the same PR. That card carried Type, Job, Customer Type, and
 *      Description. Description survives (KEEP-IT ruling this turn) via
 *      a new left-column "Work Order Details" card.
 *   2. The Schedule card's view-mode Scheduled cell (:1077-1099 pre-fix,
 *      the `date range + time` block inside the editMode ternary) —
 *      deleted in the same PR. Edit-mode Schedule card is untouched
 *      (its DatePicker + two TimePickers remain the sole editor).
 *   3. Status was NEVER a strip candidate — DetailPageHeader:957
 *      already carries `status={headerStatus || undefined}`. Duplicating
 *      it was v1's mistake, corrected in v2.
 *
 * ─── View-mode-only gate ────────────────────────────────────────────────
 *
 * The parent gates the mount on !editMode. This mirrors GoldenCustomerCard
 * at WorkOrderDetail.js:985 (per wire §B: "customer card not repeated in
 * State B"). In edit mode:
 *   - The strip disappears.
 *   - Schedule card is the sole date/time editor (unchanged).
 *   - WO Type dropdown lives in the new "Work Order Details" card's
 *     edit-mode branch, adjacent to Schedule for one-mental-model
 *     "when + type" clustering. Its save wiring (setDraft({ woType })) is
 *     unchanged — falsifier #5 covers that round-trip.
 *
 * ─── Data contract ──────────────────────────────────────────────────────
 *
 *   wo             — the WorkOrder record. Reads: woType|wo_type,
 *                    jobId|job_id, jobTitle|job_title, jobNumber,
 *                    startDate|start_date|scheduledDate,
 *                    endDate|end_date, startTime|start_time|scheduledTime,
 *                    endTime|end_time.
 *   customer       — hydrated customer record; forwarded to CustomerTypeCell.
 *   settingsCtx    — useSettingsContext result; forwarded to
 *                    CustomerTypeCell + used here for getEnumLabel.
 *   getEnumLabel   — from settingsCtx; resolves 'work_order_types' to a
 *                    display label. Fallback to humanizeEnum(rawType), then
 *                    to 'Standard' when rawType is absent. Move-as-is
 *                    contract per operator (PT-21 21.5.1 deliberate).
 *   humanizeEnum   — utility, passed in from the parent so this file
 *                    stays free of import churn on that utility.
 *   onJobClick     — optional. When provided, the Job cell is a nav
 *                    affordance; otherwise it renders as display-only.
 *                    Per ruling Q3: default rendering is display-only
 *                    (the breadcrumb at WorkOrderDetail.js:861 already
 *                    fills the nav role for the Job).
 *
 * ─── Failure-mode-attached rules ────────────────────────────────────────
 *
 *   RULE: Customer TYPE only, never customer NAME. Falsifier #2v2 catches
 *   this — CustomerTypeCell renders the settings-driven type label
 *   (Residential/Commercial/etc.); the customer's NAME lives on
 *   GoldenCustomerCard exclusively.
 *
 *   RULE: No editors here. Not a Save button, not an inline pencil, not
 *   a select. Every editor lives in edit-mode surfaces (Schedule card
 *   for date/time, new Work Order Details card for WO Type). Falsifier #5
 *   catches editor bleed.
 *
 *   RULE: No Status. Falsifier #4 catches its return.
 *
 * IDI-186 · Nevado · 2026-09-06 · corroborates: BUILD SCOPE v3 in
 *                                  Linear ticket body; WALKTHROUGH register
 *                                  §199 grouping (Type · Job · Start · Stop).
 */
export default function WorkOrderHeaderStrip({
  wo,
  customer,
  settingsCtx,
  getEnumLabel,
  humanizeEnum,
  onJobClick,
}) {
  // ─── Type ──────────────────────────────────────────────────────────
  // Move-as-is per operator's PT-21 21.5.1 note: the 'Standard' fallback
  // when rawType is absent is DELIBERATE, not a defect. Do not "fix" it
  // to em-dash or "Type not set" here.
  // IDI-440: a migrated WO stores its type as `typeId` (+ the API's `type`
  // label), not `woType`, so the strip read "Standard" for every migrated WO.
  const rawType = woTypeValue(wo);
  // IDI-470 (supersedes the PT-21 note above): an empty type reads "No type".
  // "Standard" named a type no dealer has.
  const typeLabel = woTypeLabel(wo, getEnumLabel) ||
    (humanizeEnum ? humanizeEnum(rawType) : rawType);

  // ─── Job label ─────────────────────────────────────────────────────
  // Same reader as WorkOrderDetail.js:2003 pre-retire, plus the same
  // snake_case fallback chain (defensive; production wire is camelCase
  // per IDI-238 recon but the snake_case path was in the retired code
  // and I'm preserving move-as-is).
  const jobLabel =
    wo?.jobTitle || wo?.job_title || wo?.jobNumber || wo?.job_number || 'View Job';
  const jobId = wo?.jobId || wo?.job_id;

  // ─── Start / Stop ──────────────────────────────────────────────────
  //
  // IDI-185 (2026-09-06): the strip now routes through the shared
  // formatDate / formatDateTime utilities (utils/formatDate.js:74,91) and
  // the shared canonicalFields accessors. Previously (pre-IDI-185) the
  // strip inlined a local `fmtDate` (weekday-long format via
  // toLocaleDateString) and rendered raw HH:mm time strings. Two
  // violations of formatDate.js's own rule ("any second implementation of
  // date formatting is a defect, not a shortcut") — and they made this
  // strip's WO start time render DIFFERENTLY from the WorkOrderAccordion
  // row two clicks away (F3 of IDI-185's falsifier list). Migrated here.
  //
  // Composition hazard (same as WorkOrderAccordion):
  //   · scheduledDate is EITHER "YYYY-MM-DD" (1/443 modern) OR a full
  //     ISO timestamp (442/443 legacy on tenant 270).
  //   · Extract the DATE PART via split('T')[0] before composing with
  //     a separate HH:mm — otherwise you concatenate a time onto a
  //     value that already has one.
  //   · No Z suffix on the composed string → parses as local time,
  //     which matches the way the user entered it via the DatePicker /
  //     TimePicker pair on the Schedule card edit-mode branch.
  //   · When scheduledDate has embedded time (Path B legacy) and no
  //     separate startTime, fall through to formatDateTime(scheduledDate).
  // IDI-471: same reader and formatting as the accordion row (F3).
  const sched = woSchedule(wo);
  const startDisplay = !sched.start
    ? null
    : sched.hasStartTime
      ? formatDateTime(`${sched.startDate}T${sched.startTime}`)
      : formatDate(sched.startDate);
  const endDisplay = !sched.stop
    ? null
    : sched.hasStopTime
      ? formatDateTime(`${sched.stopDate}T${sched.stopTime}`)
      : (sched.multiDay ? formatDate(sched.stopDate) : null);

  return (
    <div
      data-testid="wo-header-strip"
      className="mb-4 rounded-md border border-[color:var(--color-border)] bg-white px-4 py-3"
    >
      <dl className="grid grid-cols-1 sm:grid-cols-5 gap-4">
        {/* Type */}
        <div>
          <dt className="text-xs font-medium text-gray-500 uppercase">Type</dt>
          <dd className="text-sm text-gray-900 mt-0.5">{typeLabel}</dd>
        </div>

        {/* Job — display-only by default (breadcrumb at :861 fills nav) */}
        <div>
          <dt className="text-xs font-medium text-gray-500 uppercase">Job</dt>
          <dd className="text-sm text-gray-900 mt-0.5">
            {onJobClick && jobId ? (
              <button
                type="button"
                onClick={() => onJobClick(jobId)}
                className="text-red-600 hover:text-red-700"
              >
                {jobLabel}
              </button>
            ) : (
              jobLabel
            )}
          </dd>
        </div>

        {/* Customer Type — factored resolution ladder, not customer name.
            CustomerTypeCell renders its OWN <dt>Customer type</dt><dd>...</dd>
            fragment (see CustomerTypeCell.js:85-97). Do NOT wrap in an
            outer dt/dd here — that produces a double label AND nests <dd>
            inside <dd>, invalid markup. The wrapping <div> is intentional:
            it participates in the strip's 5-column grid layout while the
            fragment's dt/dd children provide the label/value pair. */}
        <div>
          <CustomerTypeCell
            settings={settingsCtx}
            customer={customer}
            wo={wo}
          />
        </div>

        {/* Start */}
        <div>
          <dt className="text-xs font-medium text-gray-500 uppercase">Start</dt>
          <dd className="text-sm text-gray-900 mt-0.5" data-testid="wo-strip-start">
            {startDisplay ? (
              <div className="flex items-center gap-2">
                <CalendarDaysIcon className="h-4 w-4 text-gray-400" />
                <span>{startDisplay}</span>
              </div>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </dd>
        </div>

        {/* Stop — same underlying data as Start (both derived from
            scheduledDate + separate time fields when present). The
            two cells display the same value when the WO has no
            distinct end time on the wire (Path B legacy on tenant
            270: 442/443 WOs). This matches the wire §B "start AND
            end date-time" intent — two visible cells even when the
            end reads the same. */}
        <div>
          <dt className="text-xs font-medium text-gray-500 uppercase">Stop</dt>
          <dd className="text-sm text-gray-900 mt-0.5" data-testid="wo-strip-stop">
            {endDisplay ? (
              <div className="flex items-center gap-2">
                <CalendarDaysIcon className="h-4 w-4 text-gray-400" />
                <span>{endDisplay}</span>
              </div>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
}
